import { ORDER_TYPES, ORDER_TYPES_LABELS_DISPLAY_ALIAS } from '@nandosaus/constants';
import PropTypes from 'prop-types';
import { useState } from 'react';

import { Alert } from '../../alert';
import { Flex } from '../../grid';
import { Icon } from '../../icons';
import { P } from '../../typography/new';
import { LocationDistanceAlert } from '../../location-distance-alert';
import { IFrameModal } from '../../iframe-modal';
import { logger } from '../../../utils/logger';

const options = {
  [ORDER_TYPES.PICK_UP]: {
    locationIcon: 'barci',
    orderTypeIcon: 'shoppingBagChili',
    detailIcon: 'clock',
  },
  [ORDER_TYPES.DINE_IN]: {
    locationIcon: 'barci',
    orderTypeIcon: 'cutlery',
    detailIcon: 'markerWithLine',
  },
  [ORDER_TYPES.DELIVERY]: {
    locationIcon: 'barci',
    orderTypeIcon: 'delivery',
    detailIcon: 'clock',
  },
  [ORDER_TYPES.CATERING]: {
    locationIcon: 'barci',
    orderTypeIcon: 'shoppingBagChili',
    detailIcon: 'schedule',
  },
  // Catering delivery: delivered (delivery icon) but on a scheduled date/time (schedule icon).
  [ORDER_TYPES.CATERING_DELIVERY]: {
    locationIcon: 'barci',
    orderTypeIcon: 'delivery',
    detailIcon: 'schedule',
  },
};

const OrderDetailsSummaryTemplate = ({
  orderType,
  location,
  orderDetail,
  error,
  isCheckout,
  deliveryUrl,
  locationIconOverride,
}) => {
  const [showTrackingModal, setShowTrackingModal] = useState(false);

  const orderOptions = options[orderType] ?? options[ORDER_TYPES.PICK_UP];

  if (!options[orderType]) {
    logger.warn('OrderDetailsSummaryTemplate: unknown orderType, falling back to PICK_UP', { orderType });
  }

  const { detailIcon, locationIcon: defaultLocationIcon, orderTypeIcon } = orderOptions;
  const locationIcon = locationIconOverride || defaultLocationIcon;

  const isDeliveryWithTracking =
    (orderType === ORDER_TYPES.DELIVERY || orderType === ORDER_TYPES.CATERING_DELIVERY) && deliveryUrl;

  const handleTrackingClick = () => {
    setShowTrackingModal(true);
  };

  return (
    <>
      <Flex textAlign="left" mb="9px">
        <Icon name={locationIcon} width={20} mr="8px" />
        <Flex flexDirection="column">
          <P variant="small-semi-bold">{location.streetDetails}</P>
          <P variant="extra-small">{location.suburbDetails}</P>
        </Flex>
      </Flex>

      {isCheckout && (
        <Flex mb="9px">
          <LocationDistanceAlert showInfoWhenGeoDisabled />
        </Flex>
      )}

      <Flex alignItems="center" mb="9px">
        <Icon name={orderTypeIcon} width={20} mr="8px" />
        <P variant="small-semi-bold">
          {isDeliveryWithTracking
            ? 'Delivery by UBER'
            : ORDER_TYPES_LABELS_DISPLAY_ALIAS?.[orderType] || ORDER_TYPES_LABELS_DISPLAY_ALIAS[ORDER_TYPES.PICK_UP]}
        </P>
        {isDeliveryWithTracking && (
          <P
            as="button"
            variant="small"
            ml="8px"
            textDecoration="underline"
            onClick={handleTrackingClick}
            style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
          >
            Delivery details
          </P>
        )}
      </Flex>
      {error ? (
        <Alert type="error">{error}</Alert>
      ) : (
        <Flex alignItems="center">
          <Icon name={detailIcon} width={20} mr="8px" />
          <P variant="small-semi-bold">{orderDetail}</P>
        </Flex>
      )}

      <IFrameModal
        open={showTrackingModal}
        onClose={() => setShowTrackingModal(false)}
        url={deliveryUrl}
        title="Order Tracking"
      />
    </>
  );
};

OrderDetailsSummaryTemplate.propTypes = {
  error: PropTypes.string,
  location: PropTypes.shape({
    streetDetails: PropTypes.string,
    suburbDetails: PropTypes.string,
  }).isRequired,
  orderDetail: PropTypes.string.isRequired,
  orderType: PropTypes.oneOf(Object.values(ORDER_TYPES)).isRequired,
  isCheckout: PropTypes.bool,
  deliveryUrl: PropTypes.string,
  locationIconOverride: PropTypes.string,
};

OrderDetailsSummaryTemplate.defaultProps = {
  error: null,
  isCheckout: false,
  deliveryUrl: null,
  locationIconOverride: null,
};

export { OrderDetailsSummaryTemplate };
